export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly payload: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface ApiExchange {
  request: { method: string; path: string; body?: unknown };
  response: { status: number; durationMs: number; body: unknown };
}

type ExchangeObserver = (exchange: ApiExchange) => void;
const observers = new Set<ExchangeObserver>();

export function observeExchanges(observer: ExchangeObserver): () => void {
  observers.add(observer);
  return () => observers.delete(observer);
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  headers?: HeadersInit;
  idempotent?: boolean;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers = new Headers(options.headers);
  const method = options.method ?? "GET";
  if (options.body !== undefined) headers.set("Content-Type", "application/json");
  if (options.idempotent) headers.set("Idempotency-Key", crypto.randomUUID());

  const startedAt = performance.now();
  const response = await fetch(`/api${path}`, {
    method,
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    credentials: "same-origin",
    // Statements and balances are mutable server data. Never let a browser
    // or an intermediary reuse an earlier empty statement after an import.
    cache: method === "GET" ? "no-store" : undefined,
  });
  const text = await response.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }
  const exchange: ApiExchange = {
    request: { method, path, body: options.body },
    response: { status: response.status, durationMs: Math.round(performance.now() - startedAt), body: payload },
  };
  observers.forEach((observer) => observer(exchange));

  if (!response.ok) {
    const message = typeof payload === "object" && payload !== null && "error" in payload
      ? String((payload as { error: unknown }).error)
      : `Request failed (${response.status})`;
    throw new ApiError(response.status, message, payload);
  }
  return payload as T;
}

export type ErrorScope = "item" | "collection";

export function humanError(error: unknown, scope: ErrorScope = "item"): string {
  if (error instanceof ApiError) {
    if (error.status === 401) return "Your session expired. Sign in again.";
    if (error.status === 403) return "Your session is not allowed to do that.";
    if (error.status === 404) {
      return scope === "collection"
        ? "Ledgerly could not load this collection. Check your session or permissions."
        : "Ledgerly could not find that. It may have been archived or removed.";
    }
    if (error.status === 409) return "That was already recorded. Nothing was duplicated.";
    if (error.status === 422) return "Ledgerly rejected that value. Check the amount and date.";
    if (error.status === 429) return "Too many requests at once. Wait a moment and try again.";
    if (error.status >= 500) return "The server could not complete that request. Nothing was changed.";
    return error.message;
  }
  // fetch() rejects with a bare TypeError for every transport failure, whose
  // message ("Failed to fetch", "NetworkError…") is browser wording, not ours.
  if (error instanceof TypeError || (error instanceof Error && /fetch|network|load failed/i.test(error.message))) {
    return "Ledgerly could not reach the server. Check your connection.";
  }
  return error instanceof Error && error.message ? error.message : "Something went wrong.";
}
